const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "G-TY3ZNQR2RW";
const CONSENT_KEY = "df-analytics-consent";

let initialized = false;
let configured = false;
let scriptPromise = null;
let scriptElement = null;
let lastPageKey = null;

export const getAnalyticsConsent = () => {
  try {
    return window.localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
};

const setAnalyticsConsent = (value) => {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Storage can be unavailable in strict private modes; analytics simply stays inactive after reload.
  }
};

const setAnalyticsDisabled = (disabled) => {
  window[`ga-disable-${MEASUREMENT_ID}`] = disabled;
};

const clearCookie = (name) => {
  const hostname = window.location.hostname;
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${name}=; expires=${expires}; path=/`;
  document.cookie = `${name}=; expires=${expires}; path=/; domain=${hostname}`;
  document.cookie = `${name}=; expires=${expires}; path=/; domain=.${hostname}`;
};

const clearAnalyticsCookies = () => {
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.trim().split("=")[0];
    if (name === "_ga" || name.startsWith("_ga_")) clearCookie(name);
  });
};

export const loadGoogleAnalytics = () => {
  if (getAnalyticsConsent() !== "granted") {
    return Promise.reject(new Error("Analytics consent required"));
  }
  if (initialized) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  setAnalyticsDisabled(false);
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  if (!configured) {
    window.gtag("js", new Date());
    window.gtag("config", MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      anonymize_ip: true,
    });
    configured = true;
  }

  if (scriptElement?.parentNode) scriptElement.remove();

  scriptElement = document.createElement("script");
  scriptElement.async = true;
  scriptElement.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(scriptElement);

  scriptPromise = new Promise((resolve, reject) => {
    scriptElement.addEventListener(
      "load",
      () => {
        initialized = true;
        resolve();
      },
      { once: true }
    );
    scriptElement.addEventListener(
      "error",
      () => {
        scriptPromise = null;
        scriptElement?.remove();
        scriptElement = null;
        reject(new Error("Google Analytics failed to load"));
      },
      { once: true }
    );
  });

  return scriptPromise;
};

export const enableAnalytics = async () => {
  setAnalyticsConsent("granted");
  setAnalyticsDisabled(false);
  await loadGoogleAnalytics();
  window.dispatchEvent(new Event("analytics-ready"));
};

export const disableAnalytics = () => {
  setAnalyticsConsent("denied");
  setAnalyticsDisabled(true);
  clearAnalyticsCookies();
};

export const trackPageView = (location) => {
  if (!initialized || getAnalyticsConsent() !== "granted" || typeof window.gtag !== "function") return;

  const pageKey = `${location.pathname}${location.search}${location.hash}`;
  if (pageKey === lastPageKey) return;
  lastPageKey = pageKey;

  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: `${window.location.origin}${pageKey}`,
  });
};

export const trackEvent = (name, params = {}) => {
  if (getAnalyticsConsent() !== "granted" || window[`ga-disable-${MEASUREMENT_ID}`]) return;

  const pagePath = window.location.pathname;
  const send = () => {
    if (
      !initialized ||
      getAnalyticsConsent() !== "granted" ||
      window[`ga-disable-${MEASUREMENT_ID}`] ||
      typeof window.gtag !== "function"
    ) {
      return;
    }

    if (name === "generate_lead") {
      window.gtag("event", "generate_lead", {
        form: "contact",
        page_path: pagePath,
      });
      return;
    }

    if (name === "contact_cta_click") {
      const allowedPlacements = ["navigation", "mobile_navigation", "hero", "page_cta", "footer_email", "footer_phone"];
      window.gtag("event", "contact_cta_click", {
        placement: allowedPlacements.includes(params.placement) ? params.placement : "unknown",
        destination: params.placement === "footer_email" ? "mailto" : params.placement === "footer_phone" ? "tel" : "/#kontakt",
        page_path: pagePath,
      });
    }
  };

  if (!initialized) {
    loadGoogleAnalytics().then(send).catch(() => {});
    return;
  }
  send();
};

window.enableAnalytics = enableAnalytics;
window.disableAnalytics = disableAnalytics;
